/**
 * Admin Dashboard Page
 *
 * Displays aggregate platform metrics and time-series charts.
 */

import { useState } from "react";
import {
  Pulse as Activity,
  Cpu,
  CurrencyDollar,
  FileText,
  Fire as Flame,
  Books as Library,
  TrendUp as TrendingUp,
  Trophy,
  Users,
  Warning,
  XLogo,
  Lightning as Zap,
} from "@phosphor-icons/react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Spinner } from "@/components/ui/Spinner";
import { useAdminDashboard, useAdminMetrics } from "@/hooks/useAdmin";
import { cn } from "@/utils/cn";
import { Link } from "react-router-dom";
import { hasRole } from "@/components/auth/AdminGuard";
import {
  COST_NOT_REPORTED,
  type MetricsResponse,
  type TopStreaker,
} from "@/services/admin";
import { formatMicroUsd } from "@/utils/formatting";

// ── Stat Card ──────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ElementType;
  accent?: string;
  /** When set, the whole card links into that metric's detail page. */
  to?: string;
}

function StatCard({ label, value, icon: Icon, accent, to }: StatCardProps) {
  const content = (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-lg",
          accent ?? "bg-primary/10 text-primary"
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-bold tracking-tight sm:text-xl">
          {typeof value === "number" ? value.toLocaleString() : value}
        </p>
      </div>
    </div>
  );

  const cardClass =
    "rounded-xl border border-border/50 bg-card p-4 sm:p-5 transition-colors";

  if (to) {
    return (
      <Link to={to} className={cn(cardClass, "block hover:bg-muted/20")}>
        {content}
      </Link>
    );
  }
  return <div className={cardClass}>{content}</div>;
}

/** Placeholder shown per-card while the dashboard's first fetch is in flight. */
function StatCardSkeleton() {
  return (
    <div
      className="animate-pulse rounded-xl border border-border/50 bg-card p-4 sm:p-5"
      aria-hidden="true"
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 shrink-0 rounded-lg bg-muted" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-3 w-20 rounded bg-muted" />
          <div className="h-5 w-14 rounded bg-muted" />
        </div>
      </div>
    </div>
  );
}

// ── Chart Section ──────────────────────────────────────────

const METRICS = [
  { key: "users", label: "Users" },
  { key: "sources", label: "Sources" },
  { key: "drafts", label: "Drafts" },
  { key: "generations", label: "AI Generations" },
  { key: "cost", label: "Provider Cost" },
] as const;

const PERIODS = [
  { key: "day", label: "Daily" },
  { key: "week", label: "Weekly" },
  { key: "month", label: "Monthly" },
] as const;

/** A chart value in the unit the API reported it in. */
function formatMetricValue(
  value: number,
  unit: MetricsResponse["unit"] | undefined
): string {
  return unit === "microUsd" ? formatMicroUsd(value) : value.toLocaleString();
}

function MetricsChart() {
  const [metric, setMetric] = useState<string>("users");
  const [period, setPeriod] = useState<string>("month");

  const { data, isLoading } = useAdminMetrics(period, metric);

  return (
    <div className="rounded-xl border border-border/50 bg-card p-4 sm:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-base font-semibold">Growth Trends</h2>
        <div className="flex flex-wrap gap-2">
          {/* Metric selector */}
          <div className="flex rounded-lg border border-border bg-muted/30 p-0.5 text-xs">
            {METRICS.map((m) => (
              <button
                key={m.key}
                onClick={() => setMetric(m.key)}
                className={cn(
                  "rounded-md px-2.5 py-1 font-medium transition-colors",
                  metric === m.key
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
          {/* Period selector */}
          <div className="flex rounded-lg border border-border bg-muted/30 p-0.5 text-xs">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={cn(
                  "rounded-md px-2.5 py-1 font-medium transition-colors",
                  period === p.key
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {data?.note && (
        <p className="-mt-2 mb-3 text-xs text-muted-foreground">{data.note}</p>
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart
            data={data?.data ?? []}
            margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
          >
            <defs>
              <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="hsl(var(--primary))"
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor="hsl(var(--primary))"
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(var(--border))"
              opacity={0.5}
            />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              stroke="hsl(var(--muted-foreground))"
              tickFormatter={(v: string) => {
                if (period === "month") return v.slice(5); // MM
                return v.slice(5); // MM-DD
              }}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              stroke="hsl(var(--muted-foreground))"
              allowDecimals={false}
              width={data?.unit === "microUsd" ? 72 : 60}
              tickFormatter={(v: number) => formatMetricValue(v, data?.unit)}
            />
            <Tooltip
              formatter={(v) => [
                formatMetricValue(Number(v), data?.unit),
                METRICS.find((m) => m.key === metric)?.label ?? metric,
              ]}
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
                fontSize: "12px",
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="hsl(var(--primary))"
              fill="url(#areaGradient)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ── Top Streakers Table ────────────────────────────────────

function TopStreakersTable({ streakers }: { streakers: TopStreaker[] }) {
  return (
    <div className="rounded-xl border border-border/50 bg-card p-4 sm:p-6">
      <div className="mb-4 flex items-center gap-2">
        <Trophy className="h-5 w-5 text-amber-500" />
        <h2 className="text-base font-semibold">Top Streakers</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="pb-2 pr-4 font-medium">#</th>
              <th className="pb-2 pr-4 font-medium">User</th>
              <th className="pb-2 pr-4 font-medium text-center">
                <Flame className="inline h-3.5 w-3.5 text-orange-500" /> Weekly
              </th>
              <th className="pb-2 pr-4 font-medium text-center">
                <Zap className="inline h-3.5 w-3.5 text-yellow-500" /> Daily
              </th>
              <th className="pb-2 pr-4 font-medium text-center">Level</th>
              <th className="pb-2 pr-4 font-medium text-right">XP</th>
              <th className="pb-2 font-medium text-center">Badges</th>
            </tr>
          </thead>
          <tbody>
            {streakers.map((s, i) => (
              <tr
                key={s.userId}
                className="border-b border-border/30 last:border-0"
              >
                <td className="py-2.5 pr-4 tabular-nums text-muted-foreground">
                  {i + 1}
                </td>
                <td className="py-2.5 pr-4">
                  <Link
                    to={`/users/${s.userId}`}
                    className="font-medium hover:text-primary hover:underline"
                  >
                    {s.name}
                  </Link>
                  <p className="text-[11px] text-muted-foreground truncate max-w-[180px]">
                    {s.email}
                  </p>
                </td>
                <td className="py-2.5 pr-4 text-center font-semibold tabular-nums">
                  {s.currentWeeklyStreak}
                </td>
                <td className="py-2.5 pr-4 text-center font-semibold tabular-nums">
                  {s.currentDailyStreak}
                </td>
                <td className="py-2.5 pr-4 text-center tabular-nums">
                  {s.level}
                </td>
                <td className="py-2.5 pr-4 text-right tabular-nums">
                  {s.xp.toLocaleString()}
                </td>
                <td className="py-2.5 text-center tabular-nums">
                  {s.badgeCount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────

/** How many stat cards the loaded page shows — see the skeleton grid below. */
const STAT_CARD_COUNT = 8;

export function AdminDashboardPage() {
  const { data: metrics, isLoading, error } = useAdminDashboard();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            Platform Overview
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Real-time metrics across all users and content.
          </p>
        </div>
        <div
          className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4"
          aria-busy="true"
          aria-label="Loading dashboard metrics"
        >
          {Array.from({ length: STAT_CARD_COUNT }).map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center text-sm text-destructive">
        Failed to load dashboard metrics. Please try again.
      </div>
    );
  }

  // Absent from an API older than cost tracking; see COST_NOT_REPORTED.
  const cost = metrics.costThisMonth as
    | typeof metrics.costThisMonth
    | undefined;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
          Platform Overview
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Real-time metrics across all users and content.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          label="Total Users"
          value={metrics.totalUsers}
          icon={Users}
          accent="bg-blue-500/10 text-blue-500"
          to={
            hasRole("platform_admin", "platform_support") ? "/users" : undefined
          }
        />
        <StatCard
          label="Active (24h)"
          value={metrics.activeUsers24h}
          icon={Activity}
          accent="bg-green-500/10 text-green-500"
        />
        <StatCard
          label="Sources"
          value={metrics.totalSources}
          icon={Library}
          accent="bg-orange-500/10 text-orange-500"
          to={
            hasRole("platform_admin", "platform_support")
              ? "/content?type=source"
              : undefined
          }
        />
        <StatCard
          label="Drafts"
          value={metrics.totalDrafts}
          icon={FileText}
          accent="bg-purple-500/10 text-purple-500"
          to={
            hasRole("platform_admin", "platform_support")
              ? "/content?type=draft"
              : undefined
          }
        />
        <StatCard
          label="AI Daily Avg"
          value={metrics.aiGenerationsDailyAvg}
          icon={Cpu}
          accent="bg-cyan-500/10 text-cyan-500"
          to={
            hasRole("platform_admin", "platform_analyst")
              ? "/ai-usage"
              : undefined
          }
        />
        <StatCard
          label="AI This Month"
          value={metrics.aiGenerationsThisMonth}
          icon={TrendingUp}
          accent="bg-amber-500/10 text-amber-500"
          to={
            hasRole("platform_admin", "platform_analyst")
              ? "/ai-usage"
              : undefined
          }
        />
        <StatCard
          label="AI Cost This Month"
          value={cost ? formatMicroUsd(cost.aiMicroUsd) : "—"}
          icon={CurrencyDollar}
          accent="bg-emerald-500/10 text-emerald-500"
          to={
            hasRole("platform_admin", "platform_analyst")
              ? "/ai-usage"
              : undefined
          }
        />
        <StatCard
          label="X Cost This Month"
          value={cost ? formatMicroUsd(cost.xMicroUsd) : "—"}
          icon={XLogo}
          accent="bg-zinc-500/10 text-zinc-600 dark:text-zinc-300"
          to={
            hasRole("platform_admin", "platform_analyst")
              ? "/ai-usage"
              : undefined
          }
        />
      </div>

      {!cost && (
        <p className="text-xs text-muted-foreground">{COST_NOT_REPORTED}</p>
      )}

      {cost && (cost.unpricedCalls > 0 || cost.fallbackCalls > 0) && (
        <p
          role="status"
          className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300"
        >
          <Warning className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          This month&apos;s cost is not exact: some calls have no listed rate.
          {hasRole("platform_admin", "platform_analyst") && (
            <Link
              to="/ai-usage"
              className="font-medium underline underline-offset-2"
            >
              See details
            </Link>
          )}
        </p>
      )}

      {/* Charts — only for roles with metrics access */}
      {hasRole("platform_admin", "platform_analyst") && <MetricsChart />}

      {/* ── Streak Metrics ── */}
      {metrics.streakMetrics && (
        <>
          <div>
            <h2 className="text-base font-semibold tracking-tight sm:text-lg">
              Streaks & Gamification
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
              User engagement through streaks and rewards.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <StatCard
              label="Weekly Streakers"
              value={metrics.streakMetrics.usersWithWeeklyStreak}
              icon={Flame}
              accent="bg-orange-500/10 text-orange-500"
            />
            <StatCard
              label="Daily Streakers"
              value={metrics.streakMetrics.usersWithDailyStreak}
              icon={Zap}
              accent="bg-yellow-500/10 text-yellow-500"
            />
            <StatCard
              label="Avg Weekly Streak"
              value={metrics.streakMetrics.avgWeeklyStreak}
              icon={Flame}
              accent="bg-orange-500/10 text-orange-500"
            />
            <StatCard
              label="Avg Daily Streak"
              value={metrics.streakMetrics.avgDailyStreak}
              icon={Zap}
              accent="bg-yellow-500/10 text-yellow-500"
            />
          </div>

          {/* Top Streakers */}
          {metrics.streakMetrics.topStreakers.length > 0 && (
            <TopStreakersTable streakers={metrics.streakMetrics.topStreakers} />
          )}
        </>
      )}
    </div>
  );
}
